import httpRequestHandler, {
    CancelToken,
    isCancel,
} from '../utils/httpRequestHandler';
import cacheService from './cacheService';
import CACHE_TIME from 'src/constants/cacheTime';

// Module-level Tier-2 cache for InsightQoreService responses. The cache key is
// `<methodName>:<JSON-encoded args>` and entries store { value, expiresAt }.
// Falsy values are cached (only `expiresAt` decides eviction) so that empty
// arrays / nulls returned by the API do not trigger refetch loops.
const _insightQoreServiceCache = new Map();

const _iqGet = (key) => {
    const e = _insightQoreServiceCache.get(key);
    if (!e) return undefined;
    if (Date.now() > e.expiresAt) {
        _insightQoreServiceCache.delete(key);
        return undefined;
    }
    return e.value;
};

const _iqSet = (key, value, ttl) => {
    _insightQoreServiceCache.set(key, { value, expiresAt: Date.now() + ttl });
};

/**
 * Clears the InsightQoreService module-level cache. Called from useAccount
 * signOut and the CacheLogoutSync redux observer so cached responses do not
 * leak across user sessions on the same browser tab.
 *
 * @param {string} [methodName] Optional method name to clear scoped entries.
 *                              When omitted, every cached entry is removed.
 */
export function clearInsightQoreServiceCache(methodName) {
    if (methodName) {
        for (const key of _insightQoreServiceCache.keys()) {
            if (key.startsWith(`${methodName}:`)) {
                _insightQoreServiceCache.delete(key);
            }
        }
        return;
    }
    _insightQoreServiceCache.clear();
}

class InsightQoreService {
    /** @typedef {import('../services/cacheService').CacheService} CacheService */

    /** @type {CacheService} */
    #cacheService;

    #cancelTokens = [];

    /**
     * @param {CacheService} cacheService
     */
    constructor(cacheService) {
        this.#cacheService = cacheService;
    }

    setAxiosInterceptors = ({ onLogout }) => {
        httpRequestHandler.interceptors.response.use(
            response => response,
            error => {
                if (error.response && error.response.status === 401) {
                    this.setSession(null);

                    if (onLogout) {
                        onLogout();
                    }
                }

                return Promise.reject(error);
            }
        );
    };

    setSession = accessToken => {
        if (accessToken) {
            localStorage.setItem('accessToken', accessToken);
            httpRequestHandler.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        } else {
            localStorage.removeItem('accessToken');
            delete httpRequestHandler.defaults.headers.common.Authorization;
        }
    };

    #getCacheKey = (methodName, ...args) => {
        return `${methodName}:${JSON.stringify(args)}`;
    };

    #getCachedOrFetch = async (cacheKey, fetchFn, ttl) => {
        const cached = _iqGet(cacheKey);
        if (cached !== undefined) return cached;

        const result = await fetchFn();
        _iqSet(cacheKey, result, ttl);
        return result;
    };

    // Public method to clear cache when needed (e.g., after data updates).
    // Delegates to the module-level helper so external callers and instance
    // callers stay consistent.
    clearRequestCache = (methodName = null) => {
        clearInsightQoreServiceCache(methodName ?? undefined);
    };

    getAccessToken = () => localStorage.getItem('accessToken');

    getCommissionVouchersBySalesPerson = async (
        salesperson,
        month,
        year,
        override = null,
        channel = null
    ) => {
        const { data } = override
            ? await httpRequestHandler.get(
                `/commission-voucher/commission-report/${salesperson}/${month}/${year}?override=${override}&channel=${channel}`
            )
            : await httpRequestHandler.get(
                `/commission-voucher/commission-report/${salesperson}/${month}/${year}?channel=${channel}`
            );
        return data;
    };

    getGetTieredCommissionSummaryBySalesRep = async (
        salesperson,
        month,
        year,
        atQuota,
        yoY
    ) => {
        if (!salesperson) return [];
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/tiered-commission-summary/${salesperson}/${month}/${year}?atquota=${atQuota}&yoy=${yoY}`
        );
        return data;
    };

    getCommissionVouchersDataWithSalesPeriodsAndSalesMembers = async (
        view,
        teamMember
    ) => {
        let result = [];
        if (teamMember) {
            let { data } = await httpRequestHandler.get(
                `/commission-voucher/commission-report-sales-periods/${view}/${teamMember}`
            );
            result = data;
        } else {
            let { data } = await httpRequestHandler.get(
                `/commission-voucher/commission-report-sales-periods/${view}`
            );
            result = data;
        }

        return result;
    };

    getRoofTopSales = async (roofTopId, month, year) => {
        const cacheKey = this.#getCacheKey('getRoofTopSales', roofTopId, month, year);

        return await this.#getCachedOrFetch(cacheKey, async () => {
            const { data } = await httpRequestHandler.get(
                `/commission-voucher/rooftop-sales/${roofTopId}/${month}/${year}`
            );
            return data;
        }, CACHE_TIME.FIVE_MIN);
    };

    getRoofTopSalesByProduct = async (roofTopId, month, year) => {
        const cacheKey = this.#getCacheKey('getRoofTopSalesByProduct', roofTopId, month, year);

        return await this.#getCachedOrFetch(cacheKey, async () => {
            const { data } = await httpRequestHandler.get(
                `/commission-voucher/rooftop-sales-by-product/${roofTopId}/${month}/${year}`
            );
            return data;
        }, CACHE_TIME.FIVE_MIN);
    };

    getCommissionHistory = async (salesPersonName, month, year) => {
        if (salesPersonName === undefined || salesPersonName === '' || month === undefined || year === undefined) return;

        const cacheKey = this.#getCacheKey('getCommissionHistory', salesPersonName, month, year);

        return await this.#getCachedOrFetch(cacheKey, async () => {
            const { data } = await httpRequestHandler.get(
                `/commission-voucher/commission-history/${salesPersonName}/${month}/${year}`
            );
            return data;
        }, CACHE_TIME.FIVE_MIN);
    };

    getLateRemitSummary = async (salesPersonName, month, year) => {
        const { data } = await httpRequestHandler.get(
            `/commission-voucher/late-remittance-payments/${salesPersonName}/${month}/${year}`
        );
        return data;
    };

    updateCommission = async adjustment => {
        const { data } = await httpRequestHandler.post(
            '/commission-voucher/commission-adjustment',
            adjustment
        );
        return data;
    };

    //#region Sales Team Management
    getTeamMemberCommissionTierDrafts = async (salesTeamId) => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/tier-date-range/${salesTeamId}`,
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    setTierDateRange = async ({ salesTeamId, startDate, endDate, salesCommissionTierRangeId }) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/set-tier-date-range',
                { salesTeamId, startDate, endDate, salesCommissionTierRangeId }
            );

            return res.data?.salesCommissionTierRangeId || null;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    cloneTiersToDraft = async (clonedTiers) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/clone-tiers-to-draft',
                clonedTiers
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    deleteCommissionTierDraft = async (selectedCommissionTierDraft) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/remove-tier-date-range',
                selectedCommissionTierDraft
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    publishCommissionTierDraft = async (selectedCommissionTierDraft) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/publish-draft-tiers',
                selectedCommissionTierDraft
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getSalesAgentCommissionTiersById = async teamMemberId => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/commission-tiers/${teamMemberId}`
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
        }
    };

    getSalesAgentCommissionTiers = async name => {
        const cacheKey = this.#getCacheKey('getSalesAgentCommissionTiers', name);

        return await this.#getCachedOrFetch(cacheKey, async () => {
            try {
                let res = await httpRequestHandler.get(
                    `/sales-team-management/sales-commission-tier/${name}`
                );
                return res.data ?? [];
            } catch (error) {
                console.log(error.message);
                return [];
            }
        }, CACHE_TIME.THIRTY_MIN);
    };

    getTeamMemberDraftTiers = async (salesTeamId, salesCommissionTierRangeId) => {
        if (salesTeamId === undefined || salesCommissionTierRangeId === undefined) return [];
        if (salesTeamId === '' || salesCommissionTierRangeId === '') return [];
        if (salesTeamId === -1 || salesCommissionTierRangeId === -1) return [];
        try {

            let res = await httpRequestHandler.get(
                `/sales-team-management/draft-tiers/${salesTeamId}/${salesCommissionTierRangeId}`,
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getAssociatedMembers = async (fullName, buildHierarchy = false) => {
        const cacheKey = this.#getCacheKey('getAssociatedMembers', fullName, buildHierarchy);

        return await this.#getCachedOrFetch(cacheKey, async () => {
            const { data } = await httpRequestHandler.get(
                `/sales-team-management/asr-associate-fullname/${fullName}?buildHierarchy=${buildHierarchy}`
            );
            return data;
        }, CACHE_TIME.THIRTY_MIN);
    };

    getSalesAgentRoles = async name => {
        let res = await httpRequestHandler.get(
            '/sales-team-management/sales-roles'
        );
        return res.data;
    };



    memberHasCommissionData = async teamMemberName => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/has-commission/${teamMemberName}`
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
        }
    };

    changeTiers = async (teamMemberId, tier) => {
        try {
            let res = await httpRequestHandler.post(
                '/sales-team-management/change-tier',
                { teamMemberId, tier }
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
        }
    };



    getSalesTeamMemeberAttributeByFullName = async (teamMemberFullName, month, year) => {
        try {
            if (teamMemberFullName === null) return [];
            if (teamMemberFullName === undefined || teamMemberFullName == '') return [];

            const cacheKey = this.#getCacheKey('getSalesTeamMemeberAttributeByFullName', teamMemberFullName, month, year);

            return await this.#getCachedOrFetch(cacheKey, async () => {
                let res = null;
                if (month != null && year != null) {
                    res = await httpRequestHandler.get(`/sales-team-management/team-member-attribute/${teamMemberFullName}/${month}/${year}`);
                } else {
                    res = await httpRequestHandler.get(`/sales-team-management/team-member-attribute/${teamMemberFullName}`);
                }
                return res?.data;
            }, CACHE_TIME.FIFTEEN_MIN);
        } catch (error) {
            console.log(error.message);
        }
    };
    getAssociatedTeamMembersByTeamMemberId = async teamMemberId => {

        if (teamMemberId === undefined) return [];
        let res = await httpRequestHandler.get(
            `/sales-team-management/associates/${teamMemberId}`
        );
        return res.data;

    };
    getAssociatedTeamMemberById = async teamMemberId => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/asr-associate/${teamMemberId}`
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getSalesTeamAttributes = async name => {
        let res = await httpRequestHandler.get(
            '/sales-team-management/team-member'
        );
        if (name) {
            res = await httpRequestHandler.get(
                `/sales-team-management/team-member/${name}`
            );
        }
        return res.data;
    };

    getSalesAgentCommissionOverrides = async childId => {
        let res = await httpRequestHandler.get(
            `/sales-team-management/sales-commission-overrides/${childId}`
        );
        return res.data;
    };

    createUpdateSalesTeamAttributes = async teamMemberData => {
        let res = await httpRequestHandler.post(
            '/sales-team-management/sales-agent',
            teamMemberData
        );

        return res.data;
    };

    // Floor Amount Management Methods
    getFloorAmounts = async salesTeamId => {
        const cacheKey = this.#getCacheKey('getFloorAmounts', salesTeamId);

        return this.#getCachedOrFetch(cacheKey, async () => {
            try {
                let res = await httpRequestHandler.get(
                    `/sales-team-management/floor-amounts/${salesTeamId}`
                );
                return res.data;
            } catch (error) {
                console.error('Error fetching floor amounts:', error);
                return [];
            }
        }, CACHE_TIME.THIRTY_MIN);
    };

    createFloorAmount = async (salesTeamId, floorAmountData) => {
        let res = await httpRequestHandler.post(
            `/sales-team-management/floor-amounts/${salesTeamId}`,
            floorAmountData
        );
        // Clear cache after creating new floor amount
        this.clearRequestCache('getFloorAmounts');
        this.clearRequestCache('getActiveFloorAmount');
        return res.data;
    };

    updateFloorAmount = async (salesTeamId, floorAmountId, floorAmountData) => {
        let res = await httpRequestHandler.put(
            `/sales-team-management/floor-amounts/${floorAmountId}`,
            floorAmountData
        );
        // Clear cache after updating floor amount
        this.clearRequestCache('getFloorAmounts');
        this.clearRequestCache('getActiveFloorAmount');
        return res.data;
    };

    deleteFloorAmount = async (salesTeamId, floorAmountId) => {
        let res = await httpRequestHandler.delete(
            `/sales-team-management/floor-amounts/${floorAmountId}`
        );
        // Clear cache after deleting floor amount
        this.clearRequestCache('getFloorAmounts');
        this.clearRequestCache('getActiveFloorAmount');
        return res.data;
    };

    /**
     * Get the active floor amount for a specific month/year
     * @param {number} salesTeamId - The sales team ID
     * @param {number} month - Month (1-12)
     * @param {number} year - Year (e.g., 2026)
     * @returns {Promise<number>} The active floor amount or 0 if none active
     */
    getActiveFloorAmount = async (salesTeamId, month, year) => {
        const cacheKey = this.#getCacheKey('getActiveFloorAmount', salesTeamId, month, year);

        return this.#getCachedOrFetch(cacheKey, async () => {
            try {
                // Fetch all floor amounts for this sales team
                const floorAmounts = await this.getFloorAmounts(salesTeamId);

                if (!floorAmounts || floorAmounts.length === 0) {
                    return 0;
                }

                // Filter valid records
                const validRecords = floorAmounts.filter(
                    record => record.id && record.id > 0 && record.amount != null
                );

                if (validRecords.length === 0) {
                    return 0;
                }

                // Create a date for the first day of the target month
                const targetDate = new Date(year, month - 1, 1);
                const targetPeriod = year * 100 + month;

                // Find the active floor amount for this period
                const activeRecord = validRecords.find(record => {
                    const startDate = record.startDate ? new Date(record.startDate) : null;
                    const endDate = record.endDate ? new Date(record.endDate) : null;

                    const startYear = startDate ? startDate.getFullYear() : null;
                    const startMonth = startDate ? startDate.getMonth() + 1 : null;
                    const endYear = endDate ? endDate.getFullYear() : null;
                    const endMonth = endDate ? endDate.getMonth() + 1 : null;

                    const startPeriod = startYear && startMonth ? startYear * 100 + startMonth : null;
                    const endPeriod = endYear && endMonth ? endYear * 100 + endMonth : null;

                    const isAfterStart = !startPeriod || targetPeriod >= startPeriod;
                    const isBeforeEnd = !endPeriod || targetPeriod <= endPeriod;

                    return isAfterStart && isBeforeEnd;
                });

                return activeRecord ? activeRecord.amount : 0;
            } catch (error) {
                console.error('Error fetching active floor amount:', error);
                return 0;
            }
        }, CACHE_TIME.THIRTY_MIN);
    };

    createUpdateCommissionAdjustment = async commissionAdjustments => {
        let res = await httpRequestHandler.post(
            '/commission-voucher/commission-adjustment',
            commissionAdjustments
        );

        return res.data;
    };

    createUpdateManagerSalesReps = async (managerId, assignedSalesReps) => {
        // Extract IDs from objects if needed, or pass through if already IDs
        const salesRepIds = Array.isArray(assignedSalesReps)
            ? assignedSalesReps.map(rep => typeof rep === 'object' ? rep.salesTeamId : rep)
            : [];

        let res = await httpRequestHandler.post('/sales-team-management/manager-rep-assignment', {
            managerId,
            assignedSalesReps: salesRepIds
        });

        return res.data;
    };


    getCommissionAdjustments = async (salesTeamId, month, year) => {
        let res = await httpRequestHandler.get(
            `/commission-voucher/commission-adjustment/${salesTeamId}/${month}/${year}`,
        );

        return res.data;
    };

    createUpdateTeamMemberTier = async tierData => {
        let res = await httpRequestHandler.post(
            '/sales-team-management/sales-commission-tier',
            tierData
        );

        return res.data;
    };

    createUpdateTeamMemberDraftTier = async tierData => {
        let res = await httpRequestHandler.post(
            '/sales-team-management/draft-tiers',
            tierData
        );

        return res.data;
    };

    removeTeamMemberTier = async tierData => {
        // Legacy tiers use salesCommissionsGroupId as the primary key
        const tierId = tierData.salesCommissionsGroupId ?? tierData.salesCommissionTierDraftId ?? tierData.id;
        let res = await httpRequestHandler.delete(
            `/sales-team-management/sales-commission-tier/${tierId}/${tierData.salesTeamId}`
        );

        return res.data;
    };

    removeTeamMemberDraftTier = async tierData => {
        let res = await httpRequestHandler.delete(
            `/sales-team-management/draft-tiers/${tierData.salesCommissionTierDraftId}/${tierData.salesTeamId}`
        );

        return res.data;
    };

    getDraftTiers = async (salesTeamId, salesCommissionTierRangeId) => {
        try {
            let res = await httpRequestHandler.get(
                `/sales-team-management/draft-tiers/${salesTeamId}/${salesCommissionTierRangeId}`,
            );
            return res.data;
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };
    //#endregion

    //#region Monthly Territory Review

    getMtrRevenueSummary = async (salesperson, month, year) => {
        if (salesperson === undefined) return [];

        const cacheKey = this.#getCacheKey('getMtrRevenueSummary', salesperson, month, year);

        try {
            return await this.#getCachedOrFetch(cacheKey, async () => {
                const { data } = await httpRequestHandler.get(
                    `/monthly-territory-review/revenue-mgr-view/${salesperson}/${year}/${month}`
                );
                return data;
            }, CACHE_TIME.FIVE_MIN);
        } catch (error) {
            return [];
        }
    };

    getMtrReportData = async (
        reportType,
        salesperson,
        month,
        year,
        view = '',
        viewId = '',
        agentId = '',
        forecast = '',
        productNumber = '',
        summaryType = '',
        channel = '',
        isCroView = false
    ) => {

        if (salesperson === undefined || salesperson === '') return [];
        if (Array.isArray(salesperson) && salesperson.length === 0) {
            return [];
        }
        let uri = `/monthly-territory-review/${reportType}/${salesperson}/${year}/${month}?view=${view}&viewId=${viewId}&agentId=${agentId}`;
        if (forecast && forecast !== '') {
            uri += `&forecast=${forecast}`;
        }
        if (productNumber && productNumber !== '') {
            uri += `&productNumber=${productNumber}`;
        }
        if (summaryType && summaryType !== '') {
            uri += `&summaryType=${summaryType}`;
        }
        if (channel && channel !== '') {
            uri += `&channel=${channel}`;
        }

        uri += `&isCroView=${isCroView}`;

        try {
            const { data } = await httpRequestHandler.get(
                uri
            );
            return data;
        } catch (error) {
            return [];
        }
    };

    getMtrReportDataByProductViewFirst = async (mtrReportRequest, isCroView = false) => {

        if (mtrReportRequest.salesPerson === undefined || mtrReportRequest.salesPerson === '') return [];
        if (Array.isArray(mtrReportRequest.salesPerson) && mtrReportRequest.salesPerson.length === 0) {
            return [];
        }

        var salesPerson;
        if (Array.isArray(mtrReportRequest.salesPerson)) {
            salesPerson = mtrReportRequest.salesPerson.join(',');
            mtrReportRequest.salesPerson = salesPerson;
        }


        let uri = `/monthly-territory-review/${mtrReportRequest.viewType}?isCroView=${isCroView}`;

        try {
            const { data } = await httpRequestHandler.post(
                uri, mtrReportRequest
            );
            return data;
        } catch (error) {
            return [];
        }
    };



    generateCSV = async (
        salesperson,
        month,
        year,
        view = 'NOT-SET',
        brokerCode = 'NOT-SET'
    ) => {
        if (salesperson === undefined) return [];

        try {
            const { data } = await httpRequestHandler.get(
                `/monthly-territory-review/export/${salesperson}/${year}/${month}?view=${view}&brokerCode=${brokerCode}`
            );

            const link = document.createElement('a');

            link.href = data.blobUri;
            link.download = data.blobName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            return [];
        }
    };

    getContractsByRooftopId = async (rooftopId, year, month) => {
        try {
            const { data } = await httpRequestHandler.get(
                `/monthly-territory-review/contracts/${rooftopId}/${year}/${month}`
            );
            return data;
        } catch (error) {
            return [];
        }
    };


    //#endregion

    /**
     * @param {import('axios').AxiosError} error
     * @returns {boolean}
     */
    isCancelError = error => isCancel(error);
}

const insightQoreService = new InsightQoreService(cacheService);
insightQoreService.setSession(insightQoreService.getAccessToken());

export default insightQoreService;
